import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MappaTratte, { COLORE_STATO, formattaData, formattaDataOra } from "./MappaTratte";
import logoLf23 from "../../assets/logo_lf23.png";
import "./ReportPubblico.css";

/**
 * La pagina del report come la vede il destinatario: intestazione, KPI, mappa, elenco
 * tratte e dettaglio con galleria. E' UN solo componente per la pagina pubblica e per
 * l'anteprima nel backoffice: cambia solo `fotoUrl` (proxy pubblico col token, oppure
 * proxy interno per l'anteprima). Cosi' quello che si valida e' esattamente quello che
 * si pubblica.
 *
 * Props:
 *  - report   lo snapshot (titolo, quartiere, periodo, generatoIl, kpi, mappa, tratte, note)
 *  - fotoUrl  (fotoId, size: "thumb"|"full") => url
 *  - anteprima  true = fascia "Anteprima" in cima
 */

const ETICHETTA_STATO = {
    effettuata: "Effettuata",
    non_effettuata: "Non effettuata",
    da_verificare: "Da verificare",
};
const ORDINE_STATO = { non_effettuata: 0, da_verificare: 1, effettuata: 2 };

const GIORNI_SETT = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
/** "2026-09-15" -> "mar". Costruita a mezzogiorno UTC per non slittare col fuso. */
const nomeGiorno = (iso) => {
    const d = new Date(`${iso}T12:00:00Z`);
    return Number.isNaN(d.getTime()) ? "" : GIORNI_SETT[d.getUTCDay()];
};

const nf = (n, d = 0) => (n === null || n === undefined || Number.isNaN(n)
    ? "—"
    : Number(n).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d }));

/** Valore formattato e sottotitolo di ogni KPI: il testo vive qui, i numeri nello snapshot. */
const kpiVista = (id, v) => {
    switch (id) {
        case "percentuale": return { valore: v.percentuale === null ? "—" : `${nf(v.percentuale, 1)}%`, sub: `${nf(v.tratte_completate)} su ${nf(v.tratte_previste)} tratte` };
        case "km_svolti": return { valore: nf(v.km_svolti, 2), sub: "km" };
        case "km_previsti": return { valore: nf(v.km_previsti, 2), sub: "km" };
        case "tratte_non_completate": return { valore: nf(v.tratte_non_completate), sub: v.tratte_da_verificare ? `+ ${nf(v.tratte_da_verificare)} da verificare` : null };
        default: return { valore: nf(v[id]), sub: null };
    }
};
const KPI_TONO = {
    tratte_previste: "blu", tratte_completate: "verde", tratte_non_completate: "rosso",
    percentuale: "blu", km_svolti: "verde", km_previsti: "grigio", passaggi: "verde", foto: "grigio",
};

/* Una foto puo' non esserci piu' (le immagini dell'app mobile hanno una scadenza sul
   bucket): meglio dirlo in chiaro che lasciare l'icona di immagine rotta. */
const Foto = ({ src, alt, className }) => {
    const [ko, setKo] = useState(false);
    if (ko) return <span className="rp-foto-ko">Foto non più disponibile</span>;
    return <img className={className} src={src} alt={alt} loading="lazy" onError={() => setKo(true)} />;
};

const BadgeStato = ({ stato }) => (
    <span className={`rp-badge rp-badge-${stato}`}>
        <span className="rp-dot" style={{ background: COLORE_STATO[stato] }} />
        {ETICHETTA_STATO[stato] || stato}
    </span>
);

const ReportView = ({ report, fotoUrl, anteprima = false }) => {
    const [ricerca, setRicerca] = useState("");
    const [filtroStato, setFiltroStato] = useState("tutte");
    const [selezionata, setSelezionata] = useState(null);
    const [lightbox, setLightbox] = useState(null); // { foto: [...], indice, titolo }
    const [giorno, setGiorno] = useState(null);     // impostato al primo giorno appena arrivano i dati
    const dettaglioRef = useRef(null);

    const tutte = useMemo(() => report?.tratte || [], [report]);
    const kpiPeriodo = useMemo(() => report?.kpi || { visibili: [], valori: {} }, [report]);

    /* I giorni del periodo con attività: li porta lo snapshot (calcolati alla
       pubblicazione). Per i report pubblicati prima che esistesse questa vista si
       ricavano dai passaggi, così anche quelli restano navigabili. */
    const giorni = useMemo(() => {
        if (Array.isArray(report?.giorni) && report.giorni.length) return report.giorni;
        const per = new Map();
        for (const t of tutte) {
            for (const p of t.passaggi || []) {
                if (!p.data) continue;
                if (!per.has(p.data)) per.set(p.data, { data: p.data, tratte: { previste: 0, effettuate: 0, non_effettuate: 0, da_verificare: 0 }, kmSvolti: 0 });
                const g = per.get(p.data);
                g.tratte.previste += 1;
                if (p.esito === "fatta") { g.tratte.effettuate += 1; g.kmSvolti += (t.lunghezzaMetri || 0) / 1000; }
                else if (p.esito === "non_fatta") g.tratte.non_effettuate += 1;
                else g.tratte.da_verificare += 1;
            }
        }
        return [...per.values()].sort((a, b) => (a.data < b.data ? -1 : 1));
    }, [report, tutte]);

    /* Con un giorno scelto il report diventa quello di QUELLA giornata: restano le sole
       tratte in programma quel giorno, e il loro stato è l'esito del passaggio di quel
       giorno (non il riassunto del periodo). Così la mappa si legge come il giro del
       giorno, che è il modo in cui il cliente guarda il servizio. */
    const tratte = useMemo(() => {
        if (!giorno) return [];
        const out = [];
        for (const t of tutte) {
            const p = (t.passaggi || []).find((x) => x.data === giorno);
            if (!p) continue;
            out.push({
                ...t,
                stato: p.esito === "fatta" ? "effettuata" : p.esito === "non_fatta" ? "non_effettuata" : "da_verificare",
                passaggi: [p],
                foto: (t.foto || []).filter((f) => !f.data || f.data === giorno),
            });
        }
        return out;
    }, [tutte, giorno]);

    /* La mappa mostra SEMPRE una giornata, mai il periodo tutto insieme: una tratta fatta
       lunedi' e saltata martedi' non ha un colore sensato, e in una rendicontazione un
       colore ambiguo e' peggio di nessun colore. All'apertura si parte dal primo giorno. */
    useEffect(() => {
        if (!giorni.length) return;
        setGiorno((g) => (g && giorni.some((x) => x.data === g) ? g : giorni[0].data));
    }, [giorni]);

    const datiGiorno = useMemo(() => giorni.find((g) => g.data === giorno) || null, [giorni, giorno]);

    /* Le foto con una posizione: l'app degli operatori la registra allo scatto. Vanno
       sulla mappa nel punto esatto, con il colore dello stato della loro tratta. */
    const fotoGeolocalizzate = useMemo(() => {
        const out = [];
        for (const t of tratte) {
            for (const f of t.foto || []) {
                if (f.lat == null || f.lng == null) continue;
                out.push({
                    ...f,
                    tratta: t.strada,
                    tratto: t.tratto,
                    codice: t.codice,
                    stato: t.stato,
                    thumb: fotoUrl(f.id, "thumb"),
                });
            }
        }
        return out;
    }, [tratte, fotoUrl]);
    // Le foto sulla mappa si accendono su richiesta: all'apertura la mappa deve parlare
    // delle tratte, e con centinaia di scatti i mucchietti coprirebbero le linee.
    const [mostraFoto, setMostraFoto] = useState(false);
    const [mappaEspansa, setMappaEspansa] = useState(false);
    const [selezioneDaMappa, setSelezioneDaMappa] = useState(false);

    /** Apre la galleria su un gruppo di foto (da un mucchietto sulla mappa). */
    const apriGruppoFoto = useCallback((gruppo) => {
        if (!gruppo?.length) return;
        const titolo = gruppo.length === 1
            ? [gruppo[0].indirizzo || gruppo[0].tratta, gruppo[0].tratto].filter(Boolean).join(" · ")
            : `${gruppo.length} foto in questo punto`;
        setLightbox({ foto: gruppo, indice: 0, titolo });
    }, []);

    /* I valori del periodo sono quelli congelati nello snapshot; quelli del giorno
       arrivano dal blocco `giorni`, anch'esso congelato. Nessuno dei due si ricalcola qui. */
    const kpi = useMemo(() => {
        if (!datiGiorno) return kpiPeriodo;
        const v = {
            tratte_previste: datiGiorno.tratte.previste,
            tratte_completate: datiGiorno.tratte.effettuate,
            tratte_non_completate: datiGiorno.tratte.non_effettuate,
            tratte_da_verificare: datiGiorno.tratte.da_verificare,
            percentuale: datiGiorno.tratte.previste ? Math.round((1000 * datiGiorno.tratte.effettuate) / datiGiorno.tratte.previste) / 10 : null,
            km_svolti: datiGiorno.kmSvolti,
            km_previsti: kpiPeriodo.valori?.km_previsti ?? null,
            passaggi: datiGiorno.tratte.effettuate,
            foto: tratte.reduce((n, t) => n + (t.foto || []).length, 0),
        };
        // "Km previsti" non ha senso su un singolo giorno: si toglie dalle card.
        return { ...kpiPeriodo, visibili: (kpiPeriodo.visibili || []).filter((k) => k !== "km_previsti"), valori: v };
    }, [datiGiorno, kpiPeriodo, tratte]);

    const conteggi = useMemo(() => {
        const c = { effettuata: 0, non_effettuata: 0, da_verificare: 0 };
        for (const t of tratte) c[t.stato] = (c[t.stato] || 0) + 1;
        return c;
    }, [tratte]);

    const filtrate = useMemo(() => {
        const q = ricerca.trim().toLowerCase();
        return tratte
            .filter((t) => filtroStato === "tutte" || t.stato === filtroStato)
            .filter((t) => !q || `${t.strada} ${t.tratto} ${t.codice}`.toLowerCase().includes(q))
            .sort((a, b) => (ORDINE_STATO[a.stato] ?? 9) - (ORDINE_STATO[b.stato] ?? 9)
                || a.strada.localeCompare(b.strada, "it") || a.tratto.localeCompare(b.tratto, "it"));
    }, [tratte, ricerca, filtroStato]);

    const trattaSel = useMemo(() => tratte.find((t) => t.codice === selezionata) || null, [tratte, selezionata]);

    /* Una sola funzione per tutti i modi di scegliere una tratta — click sulla mappa,
       voce dell'elenco, chiusura del dettaglio — cosi' si comportano allo stesso modo:
       chi sceglie quella gia' scelta la deseleziona. `daMappa` serve solo a non far
       saltare l'inquadratura quando il dito e' gia' sulla tratta giusta. */
    const seleziona = useCallback((codice, { daMappa = false, toggle = false } = {}) => {
        setSelezioneDaMappa(daMappa);
        setSelezionata((prev) => (codice && prev === codice ? (toggle ? null : prev) : codice || null));
    }, []);

    // Cambiando giornata la tratta aperta potrebbe non esserci più: si chiude il dettaglio.
    useEffect(() => {
        setLightbox(null);
        setSelezionata((prev) => (prev && tratte.some((t) => t.codice === prev) ? prev : null));
    }, [giorno, tratte]);

    // Sul telefono il dettaglio sta sotto la mappa: quando si sceglie una tratta lo si porta in vista.
    useEffect(() => {
        if (trattaSel && dettaglioRef.current && window.innerWidth < 900) {
            dettaglioRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [trattaSel]);

    /* Tutto schermo: la mappa e' la parte che si guarda davvero, quindi deve poter
       occupare tutto. Si usa un overlay CSS invece della Fullscreen API perche' su iOS
       quella non funziona sugli elementi, e qui serve che vada ovunque. Mentre e' aperto
       si blocca lo scorrimento della pagina sotto. */
    useEffect(() => {
        if (!mappaEspansa) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setMappaEspansa(false); };
        const overflowPrima = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = overflowPrima;
        };
    }, [mappaEspansa]);

    // Tastiera nel lightbox: frecce e Esc.
    useEffect(() => {
        if (!lightbox) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight") setLightbox((l) => l && { ...l, indice: (l.indice + 1) % l.foto.length });
            if (e.key === "ArrowLeft") setLightbox((l) => l && { ...l, indice: (l.indice - 1 + l.foto.length) % l.foto.length });
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox]);

    if (!report) return null;

    return (
        <div className="rp-root">
            {anteprima && <div className="rp-anteprima">Anteprima — questo è ciò che vedrà chi apre il link. Il report non è ancora pubblicato.</div>}

            <header className="rp-header">
                <div className="rp-header-inner">
                    <img src={logoLf23} alt="LF23" className="rp-logo" />
                    <div className="rp-header-testi">
                        <h1 className="rp-titolo">{report.titolo || `Report attività – ${report.quartiere || ""}`}</h1>
                        <div className="rp-sottotitolo">
                            <span>Periodo <strong>{formattaData(report.periodo?.dal)} – {formattaData(report.periodo?.al)}</strong></span>
                            {report.quartiere && <span>Quartiere <strong>{report.quartiere}</strong></span>}
                            <span>Generato il <strong>{formattaDataOra(report.generatoIl)}</strong></span>
                        </div>
                        {/* I numeri del periodo restano qui: le card sotto raccontano la
                            giornata scelta, e mescolare le due cose confonderebbe. */}
                        {kpiPeriodo.valori?.tratte_previste > 0 && (
                            <div className="rp-riepilogo-periodo">
                                Nel periodo: <strong>{nf(kpiPeriodo.valori.tratte_previste)}</strong> tratte previste,
                                <strong> {nf(kpiPeriodo.valori.tratte_completate)}</strong> completate
                                {kpiPeriodo.valori.percentuale !== null && <> (<strong>{nf(kpiPeriodo.valori.percentuale, 1)}%</strong>)</>}
                                {kpiPeriodo.valori.km_svolti ? <>, <strong>{nf(kpiPeriodo.valori.km_svolti, 2)} km</strong> svolti</> : null}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="rp-main">
                {report.note && <p className="rp-note">{report.note}</p>}

                {/* KPI */}
                {kpi.visibili?.length > 0 && (
                    <section className="rp-kpi" aria-label="Indicatori">
                        {kpi.visibili.map((id) => {
                            const { valore, sub } = kpiVista(id, kpi.valori || {});
                            return (
                                <div key={id} className={`rp-kpi-card tono-${KPI_TONO[id] || "grigio"}`}>
                                    <div className="rp-kpi-label">{kpi.etichette?.[id] || id}</div>
                                    <div className="rp-kpi-valore">{valore}</div>
                                    {sub && <div className="rp-kpi-sub">{sub}</div>}
                                </div>
                            );
                        })}
                    </section>
                )}

                {giorni.length > 0 && (
                    <section className="rp-giorni" aria-label="Giornate del periodo">
                        <div className="rp-giorni-intro">
                            <strong>Giorno per giorno</strong>
                            <span>Mappa, elenco e indicatori si riferiscono alla giornata scelta.</span>
                        </div>
                        <div className="rp-giorni-barra">
                            {giorni.map((g) => {
                                const perc = g.tratte.previste ? Math.round((100 * g.tratte.effettuate) / g.tratte.previste) : 0;
                                return (
                                    <button key={g.data} type="button" className={`rp-giorno ${giorno === g.data ? "attivo" : ""}`}
                                        onClick={() => setGiorno(g.data)} aria-pressed={giorno === g.data}
                                        title={`${g.tratte.effettuate} effettuate su ${g.tratte.previste} previste`}>
                                        <span className="rp-giorno-gg">{nomeGiorno(g.data)}</span>
                                        <span className="rp-giorno-data">{formattaData(g.data).slice(0, 5)}</span>
                                        <span className="rp-giorno-barra"><span style={{ width: `${perc}%` }} /></span>
                                        <span className="rp-giorno-num">{g.tratte.effettuate}/{g.tratte.previste}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {tratte.length === 0 ? (
                    <div className="rp-vuoto">
                        <strong>{giorni.length ? "Nessuna tratta in programma in questa giornata." : "Nessuna tratta in questo report."}</strong>
                        <span>{giorni.length ? "Scegli un altro giorno dalla barra qui sopra." : "Il periodo selezionato non contiene attività da mostrare."}</span>
                    </div>
                ) : (
                    <>
                        {/* Mappa + elenco */}
                        <section className={`rp-corpo ${mappaEspansa ? "con-mappa-espansa" : ""}`}>
                            <div className={`rp-colonna-mappa ${mappaEspansa ? "espansa" : ""}`}>
                                {mappaEspansa && (
                                    <div className="rp-espansa-testa">
                                        <strong>{report.titolo}</strong>
                                        <span>{giorno ? `${nomeGiorno(giorno)} ${formattaData(giorno)}` : ""}</span>
                                        {giorni.length > 1 && (
                                            <div className="rp-espansa-giorni">
                                                {giorni.map((g) => (
                                                    <button key={g.data} type="button" className={`rp-giorno-mini ${giorno === g.data ? "attivo" : ""}`}
                                                        onClick={() => setGiorno(g.data)}>
                                                        {nomeGiorno(g.data)} {formattaData(g.data).slice(0, 5)}
                                                        <span>{g.tratte.effettuate}/{g.tratte.previste}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <MappaTratte tratte={tratte} mappa={report.mappa} selezionata={selezionata}
                                    selezioneDaMappa={selezioneDaMappa} onSeleziona={seleziona}
                                    foto={mostraFoto ? fotoGeolocalizzate : []} onApriFoto={apriGruppoFoto}
                                    espansa={mappaEspansa} onEspandi={() => setMappaEspansa((v) => !v)} />
                                <div className="rp-legenda">
                                    {["effettuata", "non_effettuata", "da_verificare"].map((s) => (
                                        <button key={s} type="button"
                                            className={`rp-legenda-voce ${filtroStato === s ? "attiva" : ""}`}
                                            onClick={() => setFiltroStato((f) => (f === s ? "tutte" : s))}
                                            title={filtroStato === s ? "Mostra tutte" : `Mostra solo ${ETICHETTA_STATO[s].toLowerCase()}`}>
                                            <span className="rp-dot" style={{ background: COLORE_STATO[s] }} />
                                            {ETICHETTA_STATO[s]} <strong>{conteggi[s] || 0}</strong>
                                        </button>
                                    ))}
                                    {fotoGeolocalizzate.length > 0 && (
                                        <button type="button" className={`rp-legenda-voce rp-legenda-foto ${mostraFoto ? "attiva" : ""}`}
                                            onClick={() => setMostraFoto((v) => !v)} aria-pressed={mostraFoto}
                                            title={mostraFoto ? "Nascondi le foto dalla mappa" : "Mostra sulla mappa il punto in cui è stata scattata ogni foto"}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                                            Foto <strong>{nf(fotoGeolocalizzate.length)}</strong>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="rp-colonna-elenco">
                                <div className="rp-elenco-testa">
                                    <div className="rp-elenco-titolo">Tratte <span>{filtrate.length}{filtrate.length !== tratte.length ? ` di ${tratte.length}` : ""}</span></div>
                                    <div className="rp-cerca">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                                        <input type="search" value={ricerca} onChange={(e) => setRicerca(e.target.value)} placeholder="Cerca via o tratta" aria-label="Cerca via o tratta" />
                                    </div>
                                    <select className="rp-filtro-stato" value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)} aria-label="Filtra per stato">
                                        <option value="tutte">Tutti gli stati</option>
                                        <option value="effettuata">Effettuate</option>
                                        <option value="non_effettuata">Non effettuate</option>
                                        <option value="da_verificare">Da verificare</option>
                                    </select>
                                </div>
                                <ul className="rp-elenco">
                                    {filtrate.length === 0 && <li className="rp-elenco-vuoto">Nessuna tratta corrisponde alla ricerca.</li>}
                                    {filtrate.map((t) => (
                                        <li key={t.codice}>
                                            <button type="button"
                                                className={`rp-tratta ${selezionata === t.codice ? "attiva" : ""}`}
                                                onClick={() => seleziona(t.codice, { toggle: true })}
                                                aria-pressed={selezionata === t.codice}>
                                                <span className="rp-dot" style={{ background: COLORE_STATO[t.stato] }} />
                                                <span className="rp-tratta-testi">
                                                    <span className="rp-tratta-via">{t.strada}</span>
                                                    {t.tratto && <span className="rp-tratta-tratto">{t.tratto}</span>}
                                                </span>
                                                <span className="rp-tratta-meta">
                                                    {(t.foto || []).length > 0 && (
                                                        <span className="rp-tratta-foto" title={`${t.foto.length} foto`}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                                                            {t.foto.length}
                                                        </span>
                                                    )}
                                                    {!t.geometria
                                                        ? <span className="rp-tratta-nogeo" title="Tratta non georeferenziata: non compare sulla mappa">no mappa</span>
                                                        : t.geometriaQualita === "via-intera"
                                                            ? <span className="rp-tratta-nogeo" title="Il pezzo esatto non è delimitabile: sulla mappa c'è l'intera via">via intera</span>
                                                            : null}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        {/* Dettaglio tratta */}
                        <section ref={dettaglioRef} className={`rp-dettaglio ${trattaSel ? "" : "rp-dettaglio-vuoto"}`} aria-live="polite">
                            {!trattaSel ? (
                                <div className="rp-dettaglio-hint">Tocca una tratta sulla mappa o nell’elenco per vederne il dettaglio e le foto; toccala di nuovo per lasciarla.</div>
                            ) : (
                                <>
                                    <div className="rp-dettaglio-testa">
                                        <div>
                                            <h2 className="rp-dettaglio-via">{trattaSel.strada}</h2>
                                            {trattaSel.tratto && <div className="rp-dettaglio-tratto">{trattaSel.tratto}</div>}
                                        </div>
                                        <BadgeStato stato={trattaSel.stato} />
                                        <button type="button" className="rp-chiudi" onClick={() => seleziona(null)} aria-label="Chiudi dettaglio">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                        </button>
                                    </div>

                                    <div className="rp-dettaglio-info">
                                        {trattaSel.lunghezzaMetri != null && <div><span>Lunghezza</span><strong>{nf(trattaSel.lunghezzaMetri)} m</strong></div>}
                                        {trattaSel.fascia && <div><span>Fascia</span><strong>{trattaSel.fascia}</strong></div>}
                                        <div><span>Passaggi previsti</span><strong>{trattaSel.passaggi?.length || 0}</strong></div>
                                        <div><span>Passaggi effettuati</span><strong>{(trattaSel.passaggi || []).filter((p) => p.esito === "fatta").length}</strong></div>
                                        {!trattaSel.geometria && <div className="rp-dettaglio-nogeo">Tratta non georeferenziata: non compare sulla mappa.</div>}
                                        {trattaSel.geometriaQualita === "via-intera" && (
                                            <div className="rp-dettaglio-nogeo">Per questa tratta non è stato possibile delimitare il pezzo esatto: sulla mappa è evidenziata l’intera via.</div>
                                        )}
                                        {trattaSel.geometriaEstremi?.da && (
                                            <div className="rp-dettaglio-estremi">Tratto delimitato da <strong>{trattaSel.geometriaEstremi.da}</strong> a <strong>{trattaSel.geometriaEstremi.a}</strong>.</div>
                                        )}
                                    </div>

                                    {trattaSel.passaggi?.length > 0 && (
                                        <ul className="rp-passaggi">
                                            {trattaSel.passaggi.map((p, i) => (
                                                <li key={i} className={`rp-passaggio rp-passaggio-${p.esito}`}>
                                                    <span className="rp-dot" style={{ background: p.esito === "fatta" ? COLORE_STATO.effettuata : p.esito === "non_fatta" ? COLORE_STATO.non_effettuata : COLORE_STATO.da_verificare }} />
                                                    <span>{p.fattoIl ? formattaDataOra(p.fattoIl) : formattaData(p.data)}</span>
                                                    <span className="rp-passaggio-esito">{p.esito === "fatta" ? "effettuato" : p.esito === "non_fatta" ? "non effettuato" : "da verificare"}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {(trattaSel.foto || []).length === 0 ? (
                                        <div className="rp-galleria-vuota">Nessuna foto per questa tratta.</div>
                                    ) : (
                                        <div className="rp-galleria">
                                            {trattaSel.foto.map((f, i) => (
                                                <button key={f.id} type="button" className="rp-foto"
                                                    onClick={() => setLightbox({
                                                        foto: trattaSel.foto.map((x) => ({ ...x, tratta: trattaSel.strada, tratto: trattaSel.tratto })),
                                                        indice: i,
                                                        titolo: [trattaSel.strada, trattaSel.tratto].filter(Boolean).join(" · "),
                                                    })}
                                                    aria-label={`Apri foto ${i + 1}`}>
                                                    <Foto src={fotoUrl(f.id, "thumb")} alt={`${trattaSel.strada} – foto ${i + 1}`} />
                                                    {(f.scattataIl || f.data) && <span className="rp-foto-data">{f.scattataIl ? formattaDataOra(f.scattataIl) : formattaData(f.data)}</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </section>
                    </>
                )}
            </main>

            <footer className="rp-footer">
                <span>Report generato automaticamente da LF Realtime · {formattaDataOra(report.generatoIl)}</span>
            </footer>

            {lightbox && (
                <div className="rp-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" aria-label="Foto ingrandita">
                    <button type="button" className="rp-lightbox-chiudi" onClick={() => setLightbox(null)} aria-label="Chiudi">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                    {lightbox.foto.length > 1 && (
                        <>
                            <button type="button" className="rp-lightbox-nav prev" aria-label="Foto precedente"
                                onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, indice: (l.indice - 1 + l.foto.length) % l.foto.length })); }}>
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                            </button>
                            <button type="button" className="rp-lightbox-nav next" aria-label="Foto successiva"
                                onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, indice: (l.indice + 1) % l.foto.length })); }}>
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                            </button>
                        </>
                    )}
                    <figure className="rp-lightbox-figura" onClick={(e) => e.stopPropagation()}>
                        <Foto src={fotoUrl(lightbox.foto[lightbox.indice].id, "full")} alt={`${lightbox.titolo} – foto ${lightbox.indice + 1}`} />
                        <figcaption>
                            {lightbox.foto[lightbox.indice].indirizzo || lightbox.titolo} — foto {lightbox.indice + 1} di {lightbox.foto.length}
                            {lightbox.foto[lightbox.indice].scattataIl && ` · ${formattaDataOra(lightbox.foto[lightbox.indice].scattataIl)}`}
                        </figcaption>
                    </figure>
                </div>
            )}
        </div>
    );
};

export default ReportView;
